import { api } from '../utils/api';
import { API_ENDPOINTS } from '../constants/AppApiConstant';
import { API_BASE_URL } from '../constants/AppApiConstant';


// API Response wrapper interface
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: Record<string, string[]>;
}

// API Product interfaces (with numeric IDs)
interface ApiBilingualName {
  en: string;
  ar: string;
}

interface ApiBilingualDescription {
  en: string;
  ar: string;
}

interface ApiProduct {
  id: number;
  name: ApiBilingualName;
  description: ApiBilingualDescription;
  price: number;
  isActive: boolean;
  category: string;        // Category name (English)
  subcategory: string;    // Subcategory name (English)
  images: string[];       // Array of image URLs (relative paths)
  photos?: ApiProductPhoto[]; // Add this line to include detailed photo objects
  createdAt: string;
  updatedAt: string;
}

interface ApiProductPhoto {
  id: number;
  product_id: number;
  photo_url: string;
  display_order: number;
  created_at: string;
}

// Frontend Product interfaces (with string IDs)
export interface Product {
  id: string;
  name: {
    en: string;
    ar: string;
  };
  description: {
    en: string;
    ar: string;
  };
  price: number;
  isActive: boolean;
  category: string;        // Category name
  subcategory: string;    // Subcategory name
  images: string[];       // Array of image URLs
  photos?: { id: string; url: string }[]; // Add this to hold photo details
  createdAt: string;
  updatedAt: string;
}

export interface CreateProductRequest {
  name: {
    en: string;
    ar: string;
  };
  description: {
    en: string;
    ar: string;
  };
  price: number;
  subcategoryId: number;
  isActive?: boolean;
}

export interface UpdateProductRequest {
  name?: {
    en: string;
    ar: string;
  };
  description?: {
    en: string;
    ar: string;
  };
  price?: number;
  subcategoryId?: number;
  isActive?: boolean;
}

export interface ProductPhoto {
  id: string;
  productId: string;
  photoUrl: string;
  displayOrder: number;
  createdAt: string;
}

/**
 * Helper to unwrap API response
 */
const unwrapResponse = <T>(response: ApiResponse<T>): T => {
  if (!response || typeof response !== 'object') {
    throw new Error('Invalid API response format');
  }
  
  if (!response.success) {
    // If there are validation errors, format them nicely
    if (response.errors) {
      const errorMessages = Object.entries(response.errors)
        .flatMap(([field, messages]) => messages.map(msg => `${field}: ${msg}`))
        .join(', ');
      throw new Error(errorMessages || response.message || 'API request failed');
    }
    throw new Error(response.message || 'API request failed');
  }
  
  if (response.data === undefined || response.data === null) {
    throw new Error('API response data is missing');
  }
  
  return response.data;
};

/**
 * Convert API product (numeric IDs) to frontend product (string IDs)
 */
const convertProduct = (apiProduct: ApiProduct): Product => ({
  id: String(apiProduct.id),
  name: apiProduct.name,
  description: apiProduct.description,
  price: apiProduct.price,
  isActive: apiProduct.isActive,
  category: apiProduct.category,
  subcategory: apiProduct.subcategory,
  images: apiProduct.images.map(img => {
    // Convert relative paths to full URLs if needed
    if (img.startsWith('/')) {
      return `${API_BASE_URL}${img}`;
    }
    return img;
  }),
  photos: apiProduct.photos?.map(p => ({ // Add this block to convert photo objects
    id: String(p.id),
    url: p.photo_url.startsWith('/')
      ? `${API_BASE_URL}${p.photo_url}`
      : p.photo_url,
  })) || [],
  createdAt: apiProduct.createdAt,
  updatedAt: apiProduct.updatedAt
});

/**
 * Convert API product photo to frontend format
 */
const convertProductPhoto = (apiPhoto: ApiProductPhoto): ProductPhoto => ({
  id: String(apiPhoto.id),
  productId: String(apiPhoto.product_id),
  photoUrl: apiPhoto.photo_url.startsWith('/') 
    ? `${API_BASE_URL}${apiPhoto.photo_url}` 
    : apiPhoto.photo_url,
  displayOrder: apiPhoto.display_order,
  createdAt: apiPhoto.created_at
});

/**
 * Get all products
 */
export const getProducts = async (): Promise<Product[]> => {
  try {
    const response = await api.get<ApiResponse<ApiProduct[]>>(API_ENDPOINTS.PRODUCTS);
    const data = unwrapResponse(response);
    
    if (!data || !Array.isArray(data)) {
      console.warn('Unexpected response format from getProducts:', data);
      return [];
    }
    
    return data.map(convertProduct);
  } catch (error: any) {
    console.error('Get products error:', error);
    const errorMessage = error?.message || 'Failed to fetch products';
    throw new Error(errorMessage);
  }
};

/**
 * Get single product by ID
 */
export const getProduct = async (id: string): Promise<Product> => {
  try {
    const response = await api.get<ApiResponse<ApiProduct>>(`${API_ENDPOINTS.PRODUCTS}/${id}`);
    const data = unwrapResponse(response);
    return convertProduct(data);
  } catch (error: any) {
    console.error('Get product error:', error);
    const errorMessage = error?.message || 'Failed to fetch product';
    throw new Error(errorMessage);
  }
};

/**
 * Create new product
 */
export const createProduct = async (product: CreateProductRequest): Promise<Product> => {
  try {
    const response = await api.post<ApiResponse<ApiProduct>>(API_ENDPOINTS.PRODUCTS, product);
    const data = unwrapResponse(response);
    return convertProduct(data);
  } catch (error: any) {
    console.error('Create product error:', error);
    const errorMessage = error?.message || 'Failed to create product';
    throw new Error(errorMessage);
  }
};

/**
 * Update existing product
 */
export const updateProduct = async (id: string, product: UpdateProductRequest): Promise<Product> => {
  try {
    const response = await api.put<ApiResponse<ApiProduct>>(`${API_ENDPOINTS.PRODUCTS}/${id}`, product);
    const data = unwrapResponse(response);
    return convertProduct(data);
  } catch (error: any) {
    console.error('Update product error:', error);
    const errorMessage = error?.message || 'Failed to update product';
    throw new Error(errorMessage);
  }
};

/**
 * Delete product
 */
export const deleteProduct = async (id: string): Promise<void> => {
  try {
    await api.delete<ApiResponse<{ message: string }>>(`${API_ENDPOINTS.PRODUCTS}/${id}`);
  } catch (error: any) {
    console.error('Delete product error:', error);
    const errorMessage = error?.message || 'Failed to delete product';
    throw new Error(errorMessage);
  }
};

/**
 * Get product photos with their IDs
 */
export const getProductPhotos = async (productId: string): Promise<ProductPhoto[]> => {
  try {
    const response = await api.get<ApiResponse<ApiProductPhoto[]>>(`${API_ENDPOINTS.PRODUCTS}/${productId}/photos`);
    const data = unwrapResponse(response);
    
    if (!data || !Array.isArray(data)) {
      console.warn('Unexpected response format from getProductPhotos:', data);
      return [];
    }
    
    return data.map(convertProductPhoto);
  } catch (error: any) {
    console.error('Get product photos error:', error);
    const errorMessage = error?.message || 'Failed to fetch product photos';
    throw new Error(errorMessage);
  }
};

/**
 * Upload product photos
 */
export const uploadProductPhotos = async (productId: string, photos: File[]): Promise<ProductPhoto[]> => {
  try {
    const formData = new FormData();
    
    // Add all photos to form data
    photos.forEach(photo => {
      formData.append('photos', photo);
    });

    // Get auth token
    const token = localStorage.getItem('dawaak_token') || sessionStorage.getItem('dawaak_token');
    
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PRODUCTS}/${productId}/photos`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
        // Don't set Content-Type - let browser set it with boundary for multipart/form-data
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API Error: ${response.status}`);
    }

    const result = await response.json();
    const data = unwrapResponse(result);
    
    if (!data || !Array.isArray(data)) {
      console.warn('Unexpected response format from uploadProductPhotos:', data);
      return [];
    }
    
    return data.map(convertProductPhoto);
  } catch (error: any) {
    console.error('Upload product photos error:', error);
    const errorMessage = error?.message || 'Failed to upload photos';
    throw new Error(errorMessage);
  }
};

/**
 * Delete product photo
 */
export const deleteProductPhoto = async (photoId: string): Promise<void> => {
  try {
    await api.delete<ApiResponse<{ message: string }>>(`${API_ENDPOINTS.PRODUCTS}/photos/${photoId}`);
  } catch (error: any) {
    console.error('Delete product photo error:', error);
    const errorMessage = error?.message || 'Failed to delete photo';
    throw new Error(errorMessage);
  }
};
